import React, { useState, useEffect, useCallback, useRef } from "react";
import Footer from "../../components/footer";
import Header from "../../components/header";
import WelcomeModal from "../../components/welcome-modal";
import styled, { keyframes, css } from "styled-components";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import FunnelStepTypes from "../funnel-step/types";
import { toast } from "react-toastify";
import { isValidGuid } from "../../utils/validation";
import Guid from "guid";
import {
  createCookie,
  getIPConfig,
  getPortalCD,
  readCookie,
} from "../../utils/helpers";
import { useDispatch } from "react-redux";
import Loader from "../../components/loader";
import { useTranslation } from "react-i18next";
import {
  generateContentKey,
  removeWhitespace,
  wrapScoresWithTraitLevel,
  ROMANTIC_COMPATIBILITY_ASSESSMENT_ID,
  COLOR_BLINDNESS_TEST_ASSESSMENT_ID,
  PERSONALITY_TEST_ASSESSMENT_ID,
  AUTISM_CHILDREN_TEST_ASSESSMENT_ID,
  CHILDHOOD_TRAUMA_TEST_ASSESSMENT_ID,
  IQ_TEST_ASSESSMENT_ID,
} from "../../utils/general-config";
import { trackEvents } from "../../utils/tracking-events";
import {
  convertToLowerCase,
  getContentImage,
  getCountryCode,
  getQuizColorByIndex,
  getQuizPaddingByIndex,
  REPORT_ROUTES,
} from "../../utils/utility-functions";
import {
  traitType,
  QuestionSetType,
} from "../../services/enum/environment-enum";
import PathHelper from "../../utils/path-helper";
import { APP_NAVIGATION } from "../../utils/constants";
import useSignOut from "../../utils/sign-out";
import QuizOption from "../../components/quiz-option";
import IQQuestion from "./IQQuestion";

const PushSelectedAnswerCountToFunnelStep = (count) => ({
  type: FunnelStepTypes.SET_SELECTED_ANSWER_COUNT,
  count,
});

const PushAnswerCountToFunnelStep = (answerCount) => ({
  type: FunnelStepTypes.SET_ANSWER_COUNT,
  answerCount,
});

// const PushBackClickCountToFunnelStep = (backCount) => ({
//   type: FunnelStepTypes.SET_BACk_BUTTON_CLICK_COUNT,
//   backCount,
// });
const PushAssessmentIdToFunnelStep = (assessmentId) => ({
  type: FunnelStepTypes.SET_ASSESSMENT_ID,
  assessmentId,
});

const PushUserIdToFunnelStep = (userGUID) => ({
  type: FunnelStepTypes.SET_USERID,
  userGUID,
});

const PushAssessmentAttemptIdToFunnelStep = (assessmentAttemptId) => ({
  type: FunnelStepTypes.SET_ASSESSMENT_ATTEMPT_ID,
  assessmentAttemptId,
});
const pushResetSelectedAnswerCountToFunnelStep = () => ({
  type: FunnelStepTypes.RESET_SELECTED_ANSWER_COUNT,
});
const PushUserInformationToFunnelStep = (userInformation) => ({
  type: FunnelStepTypes.SET_USERINFO,
  payload: userInformation,
});

const PushAssessmentMapIdToFunnelStep = (assessmentMapId) => {
  return {
    type: FunnelStepTypes.SET_ASSESSMENT_MAP_ID,
    assessmentMapId,
  };
};
const PushOptionCountOfQuestionToFunnelStep = (optionCount) => {
  return {
    type: FunnelStepTypes.SET_OPTION_COUNT_OF_QUESTIONS,
    optionCount,
  };
};

const Quiz = ({
  getBulkQuestionsConnect,
  saveUserAnswerConnect,
  updateUserAnswerConnect,
  getUserAnswerByAssessmentAttemptIdAndQuestionIdConnect,
  saveAssessmentResultConnect,
  getAssessmentResultByAssessmentAttemptIdConnect,
  funnelStep,
  UpdateUserConnect,
  GetUserByIdConnect,
  getAssessmentAttemptConnect,
  createAssessmentAttemptConnect,
  updateAssessmentAttemptConnect,
  IsGuestOrPremiumUserConnect,
  getContentLatestVersionConnect,
  getAssessmentByAssessmentIdConnect,
  createContentMapConnect,
  getUserAnswerByAssessmentAttemptIdConnect,
  getCareerContent,
  getAllAssessmentConnect,
  createAssessmentMapConnect,
}) => {
  const signOut = useSignOut();
  const newUserId = Guid.create().toString();
  const newAssessmentAttemptId = Guid.create().toString();
  const toastShownRef = useRef(false);
  const [savedUserAnswer, setSavedUserAnswer] = useState([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [shuffledQuestions, setShuffledQuestions] = useState([]);
  const { t } = useTranslation();
  const [userData, setUserData] = useState(null);
  const [isPremiumOrGuest, setIsPremiumOrGuest] = useState(false);

  // const [previousAnswer, setPreviousAnswer] = useState(null);
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const {
    totalQuestions,
    selectedAnswerCount,
    userGUID,
    assessmentAttemptId,
    assessmentId,
    totalAnswerCount,
    isQuizComplete,
    countryCD: reduxCountryCD,
    currencyCD,
    ipAddress,
    isAuth,
    // backButtonClickCount,
  } = funnelStep || {};

  const countryCD = getCountryCode(reduxCountryCD);

  const ASSESSMENT_ID = isValidGuid(queryParams.get("assessmentId"))
    ? queryParams.get("assessmentId")
    : assessmentId;
  const assessmentIdFromQuery = isValidGuid(queryParams.get("assessmentId"))
    ? queryParams.get("assessmentId")
    : "";
  const assessmentMapIdFromQuery = isValidGuid(queryParams.get("assessmapid"))
    ? queryParams.get("assessmapid")
    : "";
  const parentIdFromQuery = isValidGuid(queryParams.get("prntid"))
    ? queryParams.get("prntid")
    : "";
  const fnameFromQuery = queryParams.get("fname");
  const lnameFromQuery = queryParams.get("lname");
  const emailFromQuery = queryParams.get("email");

  const isRomanticCompatibilityTest =
    convertToLowerCase(ASSESSMENT_ID) ===
    convertToLowerCase(ROMANTIC_COMPATIBILITY_ASSESSMENT_ID);

  const newAssessmentMapId =
    isRomanticCompatibilityTest && Guid.create().toString();

  const isColorBindnessAssessment =
    convertToLowerCase(ASSESSMENT_ID) ===
    convertToLowerCase(COLOR_BLINDNESS_TEST_ASSESSMENT_ID);

  const isAutismChildrenAssessment =
    convertToLowerCase(ASSESSMENT_ID) ===
    convertToLowerCase(AUTISM_CHILDREN_TEST_ASSESSMENT_ID);

  const isPersonalityTestAssessment =
    convertToLowerCase(ASSESSMENT_ID) ===
    convertToLowerCase(PERSONALITY_TEST_ASSESSMENT_ID);

  const isChildHoodTraumaTest =
    convertToLowerCase(ASSESSMENT_ID) ===
    convertToLowerCase(CHILDHOOD_TRAUMA_TEST_ASSESSMENT_ID);

  const isIQTest =
    convertToLowerCase(ASSESSMENT_ID) ===
    convertToLowerCase(IQ_TEST_ASSESSMENT_ID);

  // const token = getCookie("jwtToken");
  const { locale } = useParams();
  const currentLocale = locale ?? "en";
  const signupPath = PathHelper(APP_NAVIGATION.SINU, currentLocale);
  const planPath = PathHelper(APP_NAVIGATION.PLAN, currentLocale);
  const signupIQPath = PathHelper(APP_NAVIGATION.SINUIQ, currentLocale);
  // const defaultCurrentIndex =
  //   totalQuestions === selectedAnswerCount
  //     ? totalQuestions - 1
  //     : selectedAnswerCount || 0;

  const navigateToRoute = (
    isAuth,
    assessmentAttemptId,
    navigate,
    isRomanticCompatibilityTest = false,
  ) => {
    if (isAuth) {
      if (isPremiumOrGuest) {
        handleViewReport(assessmentAttemptId, assessId);
      } else {
        navigate(planPath.next);
      }
    } else {
      // Only change for IQ test users go to signup-iq
      if (isIQTest) {
        const signupIQPath = PathHelper(APP_NAVIGATION.SINUIQ, currentLocale);

        navigate(
          `${signupIQPath.next}?assessmentAttemptId=${assessmentAttemptId}`,
        );
        return;
      }

      // Existing flow
      navigate(
        `${signupPath.next}?assessmentAttemptId=${assessmentAttemptId}${
          isRomanticCompatibilityTest && !!parentIdFromQuery
            ? `&prntid=${parentIdFromQuery}&assessmapid=${assessmentMapIdFromQuery}`
            : ""
        }`,
      );
    }
  };

  const userIdRef = useRef(userGUID || newUserId);
  const assessmentAttemptDataIdRef = useRef(
    assessmentAttemptId || newAssessmentAttemptId,
  );

  const assessmentMapIdRef = useRef(
    assessmentMapIdFromQuery || newAssessmentMapId,
  );

  // const [visibleQuestions, setVisibleQuestions] = useState(10);
  const dispatch = useDispatch();
  const [quizData, setQuizData] = useState([]);
  const [questionSetType, setQuestionSetType] = useState(null);
  const [userAnswer, setUserAnswer] = useState(0);
  const [userAnswers, setUserAnswers] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [assesmentOptionCount, setAssessmenOptionCount] = useState(0);

  const [animationTrigger, setAnimationTrigger] = useState(false);
  const newAssessmentAttemptIdRef = useRef(null);
  const [assessId, setAssessId] = useState("");
  const navigate = useNavigate();
  const [imageLoaded, setImageLoaded] = useState({});

  const questionRefs = useRef([]);

  const isEqualQueAnsCount = totalQuestions === selectedAnswerCount;
  const isFetchedRef = useRef(false);

  // Inside the Quiz component
  const submitButtonRef = useRef(null); // Create a ref for the button

  useEffect(() => {
    if (!!ASSESSMENT_ID) {
      setIsLoading(true);
      getAllAssessmentConnect().then((response) => {
        if (!!response) {
          const matchedAssessment = response.find(
            (assessment) =>
              assessment.id.toLowerCase() === ASSESSMENT_ID.toLowerCase(),
          );
          dispatch(
            PushOptionCountOfQuestionToFunnelStep(
              parseInt(matchedAssessment?.optionCount),
            ),
          );

          setAssessmenOptionCount(parseInt(matchedAssessment?.optionCount));
          setIsLoading(false);
        }
      });
    }
  }, [getAllAssessmentConnect, ASSESSMENT_ID]);
  const hasFetched = useRef(false);

  useEffect(() => {
    if (hasFetched.current) return;

    if (
      assessmentAttemptId &&
      savedUserAnswer.length === 0 &&
      !isFetchedRef.current &&
      ASSESSMENT_ID // Make sure we have an assessment ID to work with
    ) {
      isFetchedRef.current = true; // Set flag to prevent further calls
      hasFetched.current = true;

      if (queryParams.get("newt") == null && !!newAssessmentAttemptId) {
        getUserAnswerByAssessmentAttemptIdConnect(assessmentAttemptId).then(
          (res) => {
            if (res && res.length > 0) {
              const isEqualAssessment =
                convertToLowerCase(res[0]?.assessmentId) ===
                convertToLowerCase(ASSESSMENT_ID);
              if (!isEqualAssessment) {
                // Execute the reset logic immediately instead of waiting for another useEffect
                if (!!newAssessmentAttemptId) {
                  dispatch(
                    PushAssessmentAttemptIdToFunnelStep(newAssessmentAttemptId),
                  );
                  dispatch(pushResetSelectedAnswerCountToFunnelStep());
                }
                assessmentAttemptDataIdRef.current = newAssessmentAttemptId;
                newAssessmentAttemptIdRef.current = newAssessmentAttemptId;
                setCurrentIndex(0);
                setUserAnswers({});
                setSavedUserAnswer([]);
                setIsLoading(false);
                return;
              }

              const isChildHoodTraumaTest =
                convertToLowerCase(res[0]?.assessmentId) ===
                convertToLowerCase(CHILDHOOD_TRAUMA_TEST_ASSESSMENT_ID);

              const givenAnswerData = res.map((item) => ({
                questionId: item.questionId,
                // Childhood Trauma Test UI expects answers in 1-5,
                // but API returns 0-4. Convert back for prefill.
                answer: isChildHoodTraumaTest
                  ? Number(item.answer) + 1
                  : item.answer,
              }));

              setSavedUserAnswer(givenAnswerData);
              setCurrentIndex(givenAnswerData.length);

              const userAnswersMap = givenAnswerData.reduce((acc, item) => {
                acc[item.questionId] = item.answer;
                return acc;
              }, {});

              // Merge the fetched answers with existing answers
              setUserAnswers((prevAnswers) => ({
                ...prevAnswers,
                ...userAnswersMap,
              }));
            }
          },
        );
      }
    }

    return () => {
      // Only reset flags on unmount, not on dependency changes
    };
  }, [
    assessmentAttemptId,
    savedUserAnswer.length,
    currentIndex,
    isEqualQueAnsCount,
    newAssessmentAttemptId,
    queryParams,
    getUserAnswerByAssessmentAttemptIdConnect,
    ASSESSMENT_ID,
    dispatch,
  ]);

  useEffect(() => {
    return () => {
      // Reset the flag when the component unmounts
      setUserAnswers({});
      localStorage.removeItem("userAnswers");
      localStorage.removeItem("currentIndex");
    };
  }, []);

  useEffect(() => {
    if (submitButtonRef.current && isAllQuestionsAnswered()) {
      // Scroll to the button smoothly when all questions are answered
      submitButtonRef.current.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    }
  }, [userAnswers]); // Trigger scroll when answers change and the button appears

  useEffect(() => {
    if (questionRefs.current[currentIndex]) {
      questionRefs.current[currentIndex].scrollIntoView({
        behavior: "smooth",
        block: "center", // Center the active question in the viewport
      });
    } else {
      console.log("No element found for index:", currentIndex); // Debugging info
    }
  }, [currentIndex]);

  // useEffect(() => {
  //   if (!isAuth) {
  //     signOut({ isNavigate: false });
  //   }
  // }, [isAuth]);

  useEffect(() => {
    setIsLoading(true);
    setAssessId(assessmentId);
    if (!!assessmentIdFromQuery) setAssessId(assessmentIdFromQuery);
    let ipAddressValue = ipAddress;
    if (!ipAddressValue) {
      getIPConfig(dispatch);
    }
    trackEvents("quizView", {
      userGuid: userGUID,
      eventLabel: "Page View",
      assessmentAttemptId: assessmentAttemptId,
      assessmentId: ASSESSMENT_ID,
      countryCD: countryCD,
      currencyCD: currencyCD,
      visitId: readCookie("visitGuid"),
      visitorId: readCookie("visitorGuid"),
      portalCD: getPortalCD(currentLocale),
    });
  }, []);
  useEffect(() => {
    if (isAuth && userGUID) {
      IsGuestOrPremiumUserConnect(userGUID).then((res) => {
        if (res) {
          setIsPremiumOrGuest(res?.isPremiumOrGuestUser);
        }
      });
      GetUserByIdConnect(userGUID).then((res) => {
        if (res) {
          setUserData(res);
        }
      });
    }
  }, [userGUID, isAuth]);

  const [isModalOpen, setIsModalOpen] = useState(true);

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  // useEffect(() => {
  //   const token = localStorage.getItem("jwtToken");
  //   if (!token) {
  //     setIsModalOpen(true);
  //   }
  // }, []);

  useEffect(() => {
    if (selectedAnswerCount > 0 || isAuth) {
      setIsModalOpen(false);
      setIsModalOpen(false);
    }
  }, [selectedAnswerCount, isAuth]);

  useEffect(() => {
    if (isAutismChildrenAssessment && !questionSetType) {
      if (queryParams.get("newt") == null && !!assessId) {
        getAssessmentStage();
      }
      return;
    }
    if (!!assessId) {
      setIsLoading(true);
      getBulkQuestionsConnect(
        assessId,
        currentLocale,
        questionSetType || undefined,
      )
        .then((response) => {
          if (response) {
            setQuizData(response);
            setIsLoading(false);
            dispatch(PushUserIdToFunnelStep(userIdRef.current));
            if (!!assessmentIdFromQuery) {
              dispatch(PushAssessmentIdToFunnelStep(assessmentIdFromQuery));
            }
            if (queryParams.get("newt") && !!newAssessmentAttemptId) {
              dispatch(
                PushAssessmentAttemptIdToFunnelStep(newAssessmentAttemptId),
              );
              dispatch(pushResetSelectedAnswerCountToFunnelStep());
              if (assessmentAttemptDataIdRef.current) {
                assessmentAttemptDataIdRef.current =
                  newAssessmentAttemptId.toString();
              }
              const currentUrl = new URL(window.location.href);
              currentUrl.searchParams.delete("newt"); // Remove the "newt" query param

              // Update the browser's history without reloading the page
              window.history.replaceState(null, "", currentUrl.toString());
              // var currentUrl = new URL(window.location.href);
              // if (window.location.href.indexOf("newt=true") > -1) {
              //   currentUrl.searchParams.dele("ispaypalsuccess", "true");
              //   window.history.pushState({}, "", currentUrl);
              // }
            } else {
              dispatch(
                PushAssessmentAttemptIdToFunnelStep(
                  assessmentAttemptDataIdRef.current,
                ),
              );
            }
          } else {
            setIsLoading(false);
          }
        })
        .catch((error) => {
          console.error("getBulkQuestionsConnect failed", error);
          setIsLoading(false);
        });
      if (queryParams.get("newt") == null && !!newAssessmentAttemptId) {
        getAssessmentStage();
      }
    }
  }, [assessId, isAutismChildrenAssessment, questionSetType]);

  const handleNext = () => {
    const nextQuestionIndex = currentIndex + 1;

    if (nextQuestionIndex < totalQuestions) {
      // Normal behavior for next question
      setAnimationTrigger(true);
      if (questionRefs.current[nextQuestionIndex]) {
        questionRefs.current[nextQuestionIndex].scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      }
      setCurrentIndex(nextQuestionIndex);

      // if (nextQuestionIndex % 10 === 9 && nextQuestionIndex < totalQuestions) {
      //   setVisibleQuestions(visibleQuestions + 10);
      // }

      setAnimationTrigger(false);
    }
    // No longer submitting here
  };

  const handleBack = () => {
    if (currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
    }
  };

  const handleIQNext = () => {
    const currentQuestion = shuffledQuestions[currentIndex];
    const selectedOptId = userAnswers[currentQuestion.id];
    const selectedOpt = (currentQuestion.questionOption || []).find(
      (o) => o.id === selectedOptId
    );
    handleAnswerChange(
      { target: { value: selectedOptId } },
      currentQuestion.id,
      currentIndex,
      selectedOpt?.isCorrect
    );
  };

  // const handleAnswerChange = (e, questionId, questionIndex) => {
  //   const { value } = e.target;

  //   // Update the answers in state
  //   setUserAnswers((prevAnswers) => ({
  //     ...prevAnswers,
  //     [questionId]: Number(value),
  //   }));

  //   let userAnswerData = {
  //     // assessmentAttemptId: assessmentAttemptDataIdRef.current,
  //     assessmentAttemptId: assessmentAttemptId,
  //     userId: userIdRef.current,
  //     questionId: questionId,
  //     assessmentId: assessId,
  //     answer: value,
  //   };

  //   let userStageData = {
  //     userId: userIdRef.current,
  //     asessmentId: assessId,
  //     assessmentAttemptId: assessmentAttemptId,
  //     // assessmentAttemptId: assessmentAttemptDataIdRef.current,
  //     assessmentStep: 1,
  //     resumeQuestionId: currentIndex + 1,
  //   };
  //   const isExitUserAnswer = savedUserAnswer.find(
  //     (item) => item.questionId === questionId
  //   );
  //   if (isExitUserAnswer) {
  //     updateUserAnswerConnect(userAnswerData).then(() => {});
  //     setSavedUserAnswer((prev) => {
  //       return prev.map((item) =>
  //         item.questionId === questionId
  //           ? { ...item, answer: Number(value) } // Update the answer
  //           : item
  //       );
  //     });
  //   } else {
  //     saveUserAnswerConnect(userAnswerData).then(() => {
  //       dispatch(PushAnswerCountToFunnelStep(currentIndex + 1));
  //       dispatch(PushSelectedAnswerCountToFunnelStep(currentIndex + 1));
  //       // Only call handleNext if the user is answering the current question
  //       if (questionIndex === currentIndex) {
  //         handleNext(); // Move to the next question or submit if on the last one
  //       }
  //     });
  //     if (currentIndex + 1 === 1) {
  //       createAssessmentAttemptConnect(userStageData).then((response) => {});
  //     } else {
  //       updateAssessmentAttemptConnect(userStageData).then((response) => {});
  //     }
  //   }

  //   // saveUserAnswerConnect(userAnswerData).then(() => {
  //   //   dispatch(PushAnswerCountToFunnelStep(currentIndex + 1));
  //   //   dispatch(PushSelectedAnswerCountToFunnelStep(currentIndex + 1));

  //   //   // Only call handleNext if the user is answering the current question
  //   //   if (questionIndex === currentIndex) {
  //   //     handleNext(); // Move to the next question or submit if on the last one
  //   //   }
  //   // });
  // };

  const handleAnswerChange = (e, questionId, questionIndex, isCorrect) => {
    if (isProcessing) return;

    setIsProcessing(true);
    const { value } = e.target;
    let finalAnswerValue = value;
    if (isColorBindnessAssessment || isIQTest) {
      finalAnswerValue = isCorrect ? 1 : 0;
    }
    // Update the answers in local state
    setUserAnswers((prevAnswers) => ({
      ...prevAnswers,
      [questionId]: isIQTest ? value : Number(value),
    }));

    // Prepare user answer data for API
    let userAnswerData = {
      assessmentAttemptId: assessmentAttemptId,
      userId: userIdRef.current,
      questionId: questionId,
      assessmentId: assessId,
      answer: finalAnswerValue,
    };
    let userStageData = {
      userId: userIdRef.current,
      asessmentId: assessId,
      assessmentAttemptId: assessmentAttemptId,
      // assessmentAttemptId: assessmentAttemptDataIdRef.current,
      assessmentStep: 1,
      resumeQuestionId: currentIndex + 1,
      ...(questionSetType != null && {
        questionSetType: questionSetType,
      }),
    };

    let userAssessmentMapData = {
      userId: userIdRef.current,
      assessmentId: assessId,
      assessmentAttemptId: assessmentAttemptId,
      assessmentMapId: assessmentMapIdRef.current,
    };

    const isExistingAnswer = savedUserAnswer.some(
      (item) => item.questionId === questionId,
    );

    // Decide whether to save or update the answer
    const processAnswer = isExistingAnswer
      ? updateUserAnswerConnect(userAnswerData)
      : saveUserAnswerConnect(userAnswerData);

    processAnswer
      .then(() => {
        // Update savedUserAnswer in state
        setSavedUserAnswer((prev) =>
          isExistingAnswer
            ? prev.map((item) =>
                item.questionId === questionId
                  ? { ...item, answer: Number(finalAnswerValue) }
                  : item,
              )
            : [...prev, { questionId, answer: Number(finalAnswerValue) }],
        );

        // Update funnel steps
        if (!isExistingAnswer) {
          dispatch(PushAnswerCountToFunnelStep(currentIndex + 1));
          dispatch(PushSelectedAnswerCountToFunnelStep(currentIndex + 1));
        }
        // IQ navigates directly; other tests use handleNext (scroll + animation)
        if (questionIndex === currentIndex) {
          handleNext();
        }
        // if (questionIndex === currentIndex) {
        //   if (isIQTest) {
        //     const next = currentIndex + 1;
        //     if (next <= shuffledQuestions.length - 1) {
        //       setCurrentIndex(next);
        //     } else {
        //       handleSubmit();
        //     }
        //   } else {
        //     handleNext();
        //   }
        // }

        setIsProcessing(false);
      })
      .catch(() => {
        setIsProcessing(false); // Reset processing state on error
      });

    // Create or update assessment attempt
    if (!isExistingAnswer) {
      if (currentIndex + 1 === 1) {
        createAssessmentAttemptConnect(userStageData).then((response) => {});
        if (isRomanticCompatibilityTest) {
          createAssessmentMapConnect(userAssessmentMapData).then((response) => {
            if (response) {
              dispatch(
                PushAssessmentMapIdToFunnelStep(
                  userAssessmentMapData.assessmentMapId,
                ),
              );
            }
          });
        }
      } else {
        updateAssessmentAttemptConnect(userStageData).then((response) => {});
      }
    }
    // updateStage.catch(() => {
    //   setIsProcessing(false); // Handle any errors
    // });
  };

  const getAssessmentStage = () => {
    getAssessmentAttemptConnect(
      userIdRef.current,
      assessmentAttemptDataIdRef.current,
    ).then((response) => {
      if (!!response) {
        const { resumeQuestionId, questionSetType: stageQuestionSetType } =
          response;
        if (stageQuestionSetType) {
          setQuestionSetType(stageQuestionSetType);
        }
        if (!!resumeQuestionId) {
          setCurrentIndex(resumeQuestionId);
        } else {
          setCurrentIndex(0); // Force the start of the quiz at 0 for testing
        }
      }
      setIsLoading(false);
    });
  };
  const fetchAssessmentResult = useCallback(() => {
    if (!!assessmentAttemptId) {
      return getAssessmentResultByAssessmentAttemptIdConnect(
        assessmentAttemptId,
      ).then((res) => {
        if (res) {
          return res; // Return the result here
        }
      });
    }
    return Promise.resolve(null); // Return null if `assessmentAttemptId` is falsy
  }, [assessmentAttemptId]);

  const handleSubmit = () => {
    if (isQuizComplete) {
      navigateToRoute(
        isAuth,
        assessmentAttemptId,
        navigate,
        isRomanticCompatibilityTest,
      );
    } else {
      setIsLoading(true);
      saveAssessmentResultConnect(assessmentAttemptId).then(
        async (response) => {
          const personalityId = response?.personality?.id;
          if (response) {
            if (!isPersonalityTestAssessment) {
              navigateToRoute(
                isAuth,
                assessmentAttemptId,
                navigate,
                isRomanticCompatibilityTest,
              );
            } else {
              let contentKey = "";
              if (
                response?.traitType === traitType.OPPOSITE &&
                isPersonalityTestAssessment
              ) {
                const mappedUserTrait =
                  response?.userTraits.length > 0
                    ? wrapScoresWithTraitLevel(response?.userTraits)
                    : [];
                contentKey = generateContentKey(mappedUserTrait);
              }
              if (isAuth) {
                const resultResposne = await fetchAssessmentResult();
                // Get the latest content version based on modelType and contentKey
                if (resultResposne.modelType && isPersonalityTestAssessment) {
                  getContentLatestVersionConnect({
                    modelType: resultResposne.modelType,
                    contentKey: contentKey,
                  })
                    .then((latestVersionResponse) => {
                      if (latestVersionResponse) {
                        // Proceed with content map creation using the version received
                        const contentMapData = {
                          userGuid: userGUID,
                          contentKey: contentKey,
                          modelType: removeWhitespace(resultResposne.modelType),
                          assessmentAttemptId: assessmentAttemptId,
                          version: latestVersionResponse, // Using the direct response here
                        };

                        // Ensure all required parameters are present
                        if (userGUID && contentKey && assessmentAttemptId) {
                          createContentMapConnect(contentMapData)
                            .then((res) => {
                              // Handle success, if needed
                              setIsLoading(false);
                              navigateToRoute(
                                isAuth,
                                assessmentAttemptId,
                                navigate,
                                isRomanticCompatibilityTest,
                              );
                              // navigate(ROUTE_CONSTANT.DASHBOARD);
                            })
                            .catch((err) => {
                              // Handle any errors in createContentMapConnect
                              console.error("Error creating content map:", err);
                            });
                        } else {
                          console.error(
                            "Missing required parameters for createContentMapConnect",
                          );
                        }
                      }
                    })
                    .catch((err) => {
                      // Handle any errors from getContentLatestVersionConnect
                      console.error("Error fetching latest version:", err);
                    });
                }
              } else {
                // If no token, navigate to the signup page
                navigateToRoute(
                  isAuth,
                  assessmentAttemptId,
                  navigate,
                  isRomanticCompatibilityTest,
                );
                // navigate(
                //   ROUTE_CONSTANT.SIGNUP +
                //     `?assessmentAttemptId=${assessmentAttemptId}`
                // );
              }
            }
          }
        },
      );
    }
  };

  useEffect(() => {
    if (!isRomanticCompatibilityTest || !parentIdFromQuery) return;

    // Store cookies if query values exist
    const cookiesToSet = [
      { key: "email", value: emailFromQuery, param: "email" },
      { key: "fName", value: fnameFromQuery, param: "fname" },
      { key: "lName", value: lnameFromQuery, param: "lname" },
    ];

    let shouldUpdateUrl = false;
    const newUrl = new URL(window.location.href);

    cookiesToSet.forEach(({ key, value, param }) => {
      if (value) {
        createCookie(key, value, 1);
        newUrl.searchParams.delete(param);
        shouldUpdateUrl = true;
      }
    });

    // Clean up URL params only if something was deleted
    if (shouldUpdateUrl) {
      window.history.replaceState({}, "", newUrl);
    }

    // Dispatch once after cookie setup
    dispatch(
      PushUserInformationToFunnelStep({
        parentUserGuid: parentIdFromQuery,
      }),
    );
  }, [
    isRomanticCompatibilityTest,
    parentIdFromQuery,
    emailFromQuery,
    fnameFromQuery,
    lnameFromQuery,
    dispatch,
  ]);

  const quizRef = useRef(null);

  const backSelectedAnswerKeyValue = Number(userAnswer || 0);

  // Assuming currentIndex is updated when the user goes to the next question
  useEffect(() => {
    if (totalQuestions !== selectedAnswerCount) {
      setUserAnswer(0);
    }
  }, [currentIndex, totalQuestions, selectedAnswerCount]);

  const isAllQuestionsAnswered = useCallback(() => {
    // IQ test shows one question at a time, so count only the answers that
    // were actually saved. A picked but unsubmitted option must not mark the
    // question as answered.
    if (isIQTest) {
      if (!shuffledQuestions.length) return false;
      return shuffledQuestions.every((question) =>
        savedUserAnswer.some((answer) => answer.questionId === question.id)
      );
    }
    if (totalQuestions <= 1) return false;
    return Object.keys(userAnswers).length === totalQuestions;
  }, [userAnswers, totalQuestions, savedUserAnswer, shuffledQuestions, isIQTest]);

  const renderCompleteTestButton = () => {
    return (
      <div className="flex justify-center items-center mt-7 mb-24">
        <button
          id="quiz-complete-test-button"
          onClick={handleSubmit}
          ref={submitButtonRef} // Attach the ref to the button
          className="bg-orange-600 hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500 text-white transition-colors py-3 rounded-3xl px-8 text-xl font-bold uppercase tracking-wider flex items-center gap-3"
        >
          {t("quiz.get_result_Cta")}

          <svg
            className="fill-white inline-block flex-shrink-0"
            width="24"
            height="24"
            xmlns="http://www.w3.org/2000/svg"
            fillRule="evenodd"
            clipRule="evenodd"
          >
            <path d="M12 0c-6.623 0-12 5.377-12 12s5.377 12 12 12 12-5.377 12-12-5.377-12-12-12zm0 1c-6.071 0-11 4.929-11 11s4.929 11 11 11 11-4.929 11-11-4.929-11-11-11zm4.828 11.5l-4.608 3.763.679.737 6.101-5-6.112-5-.666.753 4.604 3.747h-11.826v1h11.828z" />
          </svg>
        </button>
      </div>
    );
  };

  //Adding classes to Navbar
  useEffect(() => {
    const navbar = document.querySelector(".navbar");
    if (navbar) {
      navbar.classList.add("sticky", "top-0", "w-full", "z-[99]");
    }

    return () => {
      if (navbar) {
        navbar.classList.remove("sticky", "top-0", "w-full", "z-[99]");
      }
    };
  }, []); // Only runs on mount and unmount

  //Adding position absolute to footer on mobile
  useEffect(() => {
    const footer = document.querySelector("footer");

    if (footer) {
      footer.classList.add("hidden", "sm:block");
    }

    return () => {
      if (footer) {
        footer.classList.remove("hidden", "sm:block");
      }
    };
  }, []);

  // Adding position absolute to progress on mobile
  // useEffect(() => {
  //   const progress = document.querySelector(".progress");

  //   if (progress) {
  //     // Remove all existing classes
  //     progress.className = "";

  //     // Add Tailwind classes for desktop (with `sm:` prefix)
  //     progress.classList.add(
  //       "sm:w-full", // width: 100%;
  //       "sm:max-w-4xl", // max-width: 50rem (4xl is approximately 50rem in Tailwind)
  //       "bg-gray-100",
  //       "rounded-0", // Custom class for background color (you can define this in your Tailwind config)
  //       "sm:rounded-lg", // border-radius: 0.5rem
  //       "overflow-hidden",
  //       "sm:h-8", // height: 2rem
  //       "sm:relative", // position: relative
  //       "sm:bottom-0",
  //       "h-5",
  //       "fixed",
  //       "bottom-0",
  //       "mx-auto",
  //       "max-w-full",
  //       "w-full",
  //       "left-0",
  //       "sm:bg-gray-300",
  //       "right-0",
  //       "flex",
  //       "items-center",
  //       "px-5",
  //       "py-8",
  //       "sm:px-0",
  //       "sm:py-0"
  //     );
  //   }

  //   return () => {
  //     if (progress) {
  //       // Clear all classes when the component is unmounted
  //       progress.className = "";
  //     }
  //   };
  // }, []);

  // // Adding position absolute to progress on mobile
  // useEffect(() => {
  //   const progress_text = document.querySelector(".progress_text");

  //   if (progress_text) {
  //     // Remove all existing classes
  //     progress_text.className = "";

  //     // Add Tailwind classes for desktop (with `sm:` prefix)
  //     progress_text.classList.add(
  //       "relative", // position: absolute;
  //       "top-0", // top: 0;
  //       "sm:left-2", // left: 0.5rem;
  //       "h-full", // height: 100%;
  //       "flex", // display: flex;
  //       "items-center", // align-items: center;
  //       "text-gray-500", // color: var(--bg-white);
  //       "font-semibold", // font-weight: 600;
  //       "pointer-events-none", // pointer-events: none;
  //       "z-10",
  //       "sm:absolute",
  //       "sm:text-white",
  //       "w-2/6",
  //       "sm:w-full",
  //       "sm:w-full",
  //       "sm:text-lg",
  //       "text-sm",
  //       "left-0"
  //     );
  //   }

  //   return () => {
  //     if (progress_text) {
  //       // Clear all classes when the component is unmounted
  //       progress_text.className = "";
  //     }
  //   };
  // }, []);

  // // Adding position absolute to progress on mobile
  // useEffect(() => {
  //   const pro_foreground = document.querySelector(".pro_foreground");

  //   if (pro_foreground) {
  //     // Remove all existing classes
  //     pro_foreground.className = "";

  //     // Add Tailwind classes for desktop (with `sm:` prefix)
  //     pro_foreground.classList.add(
  //       "h-1.5",
  //       "sm:h-full",
  //       "transition-all",
  //       "duration-500",
  //       "bg-[#7cd0e1]",
  //       "rounded-l-md"
  //     );
  //   }

  //   return () => {
  //     if (pro_foreground) {
  //       // Clear all classes when the component is unmounted
  //       pro_foreground.className = "";
  //     }
  //   };
  // }, []);

  // // Adding position absolute to progress on mobile
  // useEffect(() => {
  //   const BackdropFore = document.querySelector(".BackdropFore");

  //   if (BackdropFore) {
  //     // Remove all existing classes
  //     BackdropFore.className = "";

  //     // Add Tailwind classes for desktop (with `sm:` prefix)
  //     BackdropFore.classList.add(
  //       "h-1.5",
  //       "bg-gray-400",
  //       "sm:ml-0",
  //       "w-full",
  //       "block",
  //       "sm:hidden",
  //       "rounded-r-md"
  //     );
  //   }

  //   return () => {
  //     if (BackdropFore) {
  //       // Clear all classes when the component is unmounted
  //       BackdropFore.className = "";
  //     }
  //   };
  // }, []);

  //moving logo on mobile
  useEffect(() => {
    const footer = document.querySelector(".logo_it");

    if (footer) {
      footer.classList.add("mx-auto", "scale-75", "sm:scale-100");
    }

    return () => {
      if (footer) {
        footer.classList.remove("mx-auto", "scale-75", "sm:scale-100");
      }
    };
  }, []);

  // Add a state for tracking gender selection
  const [gender, setGender] = useState("");

  // Function to handle gender selection
  const handleGenderSelection = (e) => {
    localStorage.setItem("gender", e.target.value);
    setGender(e.target.value);
    if (userData && !userData?.gender) {
      const data = {
        ...userData,
        gender: e.target.value || userData?.gender,
      };

      UpdateUserConnect(data).then((res) => {});
    }
  };

  // Check if gender is selected
  // const isGenderSelected = () => {
  //   return gender !== ""; // Returns true if a gender is selected
  // };

  // Ref for gender section
  const genderSectionRef = useRef(null);

  const handleViewReport = (assessmentAttemptId, assessmentId) => {
    let reportRoute;

    if (isRomanticCompatibilityTest) {
      const romanticRoutes = REPORT_ROUTES[assessmentId];
      const reportType = parentIdFromQuery ? "compatibility" : "personal";
      if (reportType === "personal") {
        reportRoute = romanticRoutes?.personal;
      } else {
        // Default to compatibility report (when reportType is "compatibility" or null)
        reportRoute = romanticRoutes?.compatibility;
      }
    } else {
      reportRoute = REPORT_ROUTES[assessmentId];
    }
    // Dynamically get the route for the given assessmentId
    if (!reportRoute) {
      console.error(`No route found for assessmentId: ${assessmentId}`);
      return;
    }
    const path = PathHelper(reportRoute, currentLocale);
    navigate(`${path.next}?aai=${assessmentAttemptId}&uid=${userGUID}`); // for example
  };

  // Add effect to scroll to gender section once all questions are completed
  useEffect(() => {
    if (isAllQuestionsAnswered() && genderSectionRef.current) {
      genderSectionRef.current.scrollIntoView({
        behavior: "smooth",
        block: "center", // Adjust based on how you want it to align
      });
    }
  }, [userAnswers]); // Triggers when answers change

  // Function to render gender section
  const renderGenderSection = () => (
    <div
      className="flex flex-col justify-center items-center mt-3 h-60 max-w-3xl sm:max-w-full mb-20 px-4"
      ref={genderSectionRef}
    >
      <p className="mb-3 text-base text-center sm:text-2xl font-semibold text-[#444C5E] max-w-2xl w-full">
        {t("quiz.gender_text")}
      </p>
      <p className="text-gray-500 text-base text-center">
        {t("quiz.gender_content_one")}
        <br />
        {t("quiz.gender_content_two")}
      </p>
      <div className="flex flex-wrap gap-4 justify-center items-center mt-7 w-full">
        <label
          className={`cursor-pointer px-6 py-2 border-2 text-gray-700 font-bold ${
            gender === "male"
              ? "bg-[#805AED] text-white"
              : "bg-white text-[#805AED]"
          } border-[#805AED] rounded-3xl text-center`}
        >
          <input
            id="quiz_gender_male"
            type="radio"
            name="gender"
            value="male"
            className="hidden"
            checked={gender === "male"}
            onChange={handleGenderSelection}
          />
          {t("quiz.male")}
        </label>

        <label
          className={`cursor-pointer px-6 py-2 border-2 text-gray-700 font-bold ${
            gender === "female"
              ? "bg-[#805AED] text-white"
              : "bg-white text-[#805AED]"
          } border-[#805AED] rounded-3xl text-center`}
        >
          <input
            id="quiz_gender_female"
            type="radio"
            name="gender"
            value="female"
            className="hidden"
            checked={gender === "female"}
            onChange={handleGenderSelection}
          />
          {t("quiz.female")}
        </label>

        <label
          className={`cursor-pointer px-6 py-2 border-2 text-gray-700 font-bold ${
            gender === "other"
              ? "bg-[#805AED] text-white"
              : "bg-white text-[#805AED]"
          } border-[#805AED] rounded-3xl text-center`}
        >
          <input
            id="quiz_gender_other"
            type="radio"
            name="gender"
            value="other"
            className="hidden"
            checked={gender === "other"}
            onChange={handleGenderSelection}
          />
          {t("quiz.other")}
        </label>
      </div>
    </div>
  );

  //Add relative to root
  useEffect(() => {
    const root = document.querySelector("#root");

    if (root) {
      root.classList.add("relative"); // Add 'relative' to root to position child elements
    }

    return () => {
      if (root) {
        root.classList.remove("relative"); // Clean up by removing the 'relative' class
      }
    };
  }, []);

  // Save currentIndex to localStorage whenever it changes
  useEffect(() => {
    if (currentIndex !== null && currentIndex >= 0) {
      localStorage.setItem("currentIndex", currentIndex);
    }
  }, [currentIndex]);

  // Save user answers to localStorage whenever they change
  useEffect(() => {
    if (userAnswers && Object.keys(userAnswers).length > 0) {
      localStorage.setItem("userAnswers", JSON.stringify(userAnswers));
    }
  }, [userAnswers]);

  // Restore state (currentIndex and userAnswers) once quizData is loaded
  useEffect(() => {
    if (quizData.length > 0) {
      const savedAnswers = localStorage.getItem("userAnswers");
      const savedIndex = localStorage.getItem("currentIndex");

      if (savedAnswers) {
        try {
          const parsedAnswers = JSON.parse(savedAnswers);
          setUserAnswers(parsedAnswers);

          // Restore currentIndex based on the number of answered questions
          const answeredQuestions = Object.keys(parsedAnswers).length;
          setCurrentIndex(Number(savedIndex) || answeredQuestions); // Prefer the saved index, or fallback to answered questions count
        } catch (error) {
          console.error("Failed to parse userAnswers from localStorage", error);
        }
      }
    }
  }, [quizData]);

  useEffect(() => {
    if (questionRefs.current[currentIndex]) {
      questionRefs.current[currentIndex].scrollIntoView({
        behavior: "smooth",
        block: "center", // Center the active question in the viewport
      });
    }
  }, [currentIndex, quizData, savedUserAnswer.length > 0]); // Ensure it runs after quizData is loaded

  useEffect(() => {
    // Remove currentIndex and userAnswers from localStorage when the quiz is completed
    if (isQuizComplete) {
      localStorage.removeItem("currentIndex");
      localStorage.removeItem("userAnswers");
    }
  }, [isQuizComplete]);

  useEffect(() => {
    const sortByOptionCount = (questions) => {
      if (!isChildHoodTraumaTest || !questions?.length) return questions ?? [];
      const effectiveCount = (q) => {
        const parsed = parseInt(q.optionCount);
        return isNaN(parsed) ? assesmentOptionCount : parsed;
      };
      return [...questions].sort(
        (a, b) => effectiveCount(a) - effectiveCount(b),
      );
    };

    if (savedUserAnswer.length > 0) {
      // Separate answered and unanswered questions
      const answered = quizData?.filter((question) =>
        savedUserAnswer.some(
          (savedAnswer) => savedAnswer.questionId === question.id,
        ),
      );
      const unanswered = quizData?.filter(
        (question) =>
          !savedUserAnswer.some(
            (savedAnswer) => savedAnswer.questionId === question.id,
          ),
      );

      // Combine answered questions first, followed by unanswered questions
      const reorderedQuestions = [...answered, ...unanswered];

      // Set the reordered list of questions
      setShuffledQuestions(sortByOptionCount(reorderedQuestions));
    } else {
      setShuffledQuestions(sortByOptionCount(quizData) ?? quizData);
    }
  }, [
    quizData,
    savedUserAnswer.length > 0,
    isChildHoodTraumaTest,
    assesmentOptionCount,
  ]);

  useEffect(() => {
    if (!isColorBindnessAssessment) return; // ✅ short-circuit early

    const isComplete =
      totalQuestions > 0 && totalQuestions === totalAnswerCount;

    // Check if this is a resumed assessment (not a new test)
    const isResumedAssessment =
      queryParams.get("newt") == null && !!assessmentAttemptId;

    // Check if user had previous answers (indicating they resumed from dashboard)
    const hadPreviousAnswers = savedUserAnswer.length > 0;

    // Check if user is coming from dashboard with all questions already completed
    // This means they completed the assessment previously and are returning from dashboard
    const isReturningFromDashboard =
      isComplete &&
      hadPreviousAnswers &&
      savedUserAnswer.length === totalQuestions;

    // Check if any questions are currently hidden (shouldShow logic)
    // This indicates user completed all questions at once without navigation
    const hasHiddenQuestions = shuffledQuestions.some((_, index) => {
      const shouldShow =
        !isColorBindnessAssessment ||
        currentIndex === index ||
        (isAllQuestionsAnswered() &&
          !isColorBindnessAssessment &&
          index === shuffledQuestions.length - 1);
      return !shouldShow;
    });

    // Only show popup if user is returning from dashboard with completed assessment
    // NOT when they're actively completing the last question
    // AND NOT when they completed all questions at once (hasHiddenQuestions)
    if (
      !toastShownRef.current &&
      isResumedAssessment &&
      isReturningFromDashboard &&
      !hasHiddenQuestions
    ) {
      toast.success(t("quiz.assessment_complete"), {
        position: "top-center",
        autoClose: 5000,
      });
      toastShownRef.current = true;
    }
  }, [
    isColorBindnessAssessment,
    totalAnswerCount,
    totalQuestions,
    t,
    queryParams,
    assessmentAttemptId,
    savedUserAnswer.length,
    shuffledQuestions,
    currentIndex,
    isAllQuestionsAnswered,
  ]);

  return (
    <div className="min-h-screen flex flex-col">
      <WelcomeModal isOpen={isModalOpen} onClose={handleCloseModal} />
      <Header />
      <div
        className="flex-1 overflow-y-auto"
        style={isIQTest ? { background: "#F2F3F6" } : undefined}
      >
        <div
          className={`flex justify-center items-center min-h-full ${
            isColorBindnessAssessment
              ? isAllQuestionsAnswered()
                ? "overflow-y-auto"
                : "overflow-hidden"
              : ""
          }`}
          style={{
            minHeight: "calc(100vh - 160px)",
            ...(isColorBindnessAssessment ? { overflowX: "hidden" } : {}),
          }}
          ref={quizRef}
        >
          <Loader isLoading={isLoading} />
          {isAutismChildrenAssessment && !questionSetType && (
            <div className="flex flex-col items-center justify-center min-h-[60vh] gap-6 px-4 py-10 max-w-2xl mx-auto">
              <p className="text-sm text-[#6B7280] text-center leading-relaxed border border-[#E5E7EB] rounded-xl px-5 py-4 bg-[#F9FAFB]">
                {t("quiz.autism_age_disclaimer")}
              </p>
              <div>
                {" "}
                <h2 className="text-2xl font-semibold text-[#444C5E] text-center">
                  {t("quiz.autism_age_question")}{" "}
                </h2>
                <p className="text-base font-normal text-[#6B7280] text-center">
                  ({t("quiz.autism_age_hint")})
                </p>
              </div>

              <div className="flex flex-col gap-3 w-full">
                <button
                  className="w-full px-8 py-4 rounded-full text-sm sm:text-lg font-medium bg-[#805AED] text-white hover:bg-[#6b47d4] transition-all duration-300 text-center"
                  onClick={() => setQuestionSetType(QuestionSetType.CHILD)}
                >
                  {t("quiz.autism_age_child_option")}
                </button>
                <button
                  className="w-full px-8 py-4 rounded-full text-sm sm:text-lg font-medium bg-[#805AED] text-white hover:bg-[#6b47d4] transition-all duration-300 text-center"
                  onClick={() => setQuestionSetType(QuestionSetType.TEEN)}
                >
                  {t("quiz.autism_age_teen_option")}
                </button>
              </div>
            </div>
          )}
          <Container
            isColorBlind={isColorBindnessAssessment}
            isIQTest={isIQTest}
            style={{
              display:
                isAutismChildrenAssessment && !questionSetType
                  ? "none"
                  : undefined,
            }}
          >
            <div
              className={`quiz-container ${
                isColorBindnessAssessment ? "w-screen" : "w-full"
              }`}
              style={{
                overflowY: isColorBindnessAssessment
                  ? isAllQuestionsAnswered()
                    ? "visible"
                    : "hidden"
                  : "auto",
              }}
              ref={quizRef}
            >
              {shuffledQuestions.map((question, index) => {
                const shouldShow =
                  (!isColorBindnessAssessment && !isIQTest) ||
                  currentIndex === index ||
                  (isAllQuestionsAnswered() &&
                    !isColorBindnessAssessment &&
                    !isIQTest &&
                    index === shuffledQuestions.length - 1);

                const optionCount = !!question.optionCount
                  ? question.optionCount
                  : assesmentOptionCount;

                return (
                  <QuestionContainer
                    ref={(el) => {
                      if (el) {
                        questionRefs.current[index] = el; // Ensure each question gets a ref
                      }
                    }}
                    className={
                      currentIndex === index ||
                      (isColorBindnessAssessment &&
                        isAllQuestionsAnswered() &&
                        index === shuffledQuestions.length - 1)
                        ? "opacity-100 scale-90 lg:scale-100 transition-all duration-500 ease-in-out sm:border-0"
                        : "opacity-20 scale-[85%] lg:scale-90 transition-all duration-500 ease-in-out border-b border-gray-400 sm:border-0"
                    } // Add class for active or inactive question
                    style={{
                      display: shouldShow ? "block" : "none",
                    }}
                    key={question.id}
                  >
                    <form
                      className={
                        isColorBindnessAssessment
                          ? "flex justify-center items-center sm:items-center container max-w-none w-full flex-col lg:flex-row gap-8 sm:gap-16"
                          : isIQTest
                            ? "w-full"
                            : "flex justify-center items-center sm:items-center container min-h-60 max-w-3xl sm:max-w-full mt-15px flex-col"
                      }
                    >
                      {isIQTest ? (
                        <IQQuestion
                          question={question}
                          currentIndex={currentIndex}
                          totalQuestions={shuffledQuestions.length}
                          userAnswers={userAnswers}
                          isQuizComplete={isQuizComplete}
                          isProcessing={isProcessing}
                          currentLocale={currentLocale}
                          onSelectAnswer={(questionId, optionId) =>
                            setUserAnswers((prev) => ({
                              ...prev,
                              [questionId]: optionId,
                            }))
                          }
                          onNext={handleIQNext}
                          onBack={handleBack}
                        />
                      ) : (
                        <>
                          <Question
                            className="text-center mb-2"
                            animation={animationTrigger}
                          >
                            {isColorBindnessAssessment ? (
                              <div className="relative w-full max-w-lg mx-auto max-h-[90vh] min-h-[400px] flex items-center justify-center">
                                {!imageLoaded[question.id] && (
                                  <Loader isLoading={true} />
                                )}
                                <img
                                  src={getContentImage(question?.mediaUrl)}
                                  alt="Colorblind test image"
                                  className={`w-full h-full object-contain transition-opacity duration-300 ${
                                    imageLoaded[question.id]
                                      ? "opacity-100"
                                      : "opacity-0"
                                  }`}
                                  aria-hidden="true"
                                  loading="eager"
                                  onLoad={() =>
                                    setImageLoaded((prev) => ({
                                      ...prev,
                                      [question.id]: true,
                                    }))
                                  }
                                />
                              </div>
                            ) : (
                              <h5
                                id={`quiz-question-${index + 1}`}
                                className={`font-semibold text-[#444C5E] ${
                                  isColorBindnessAssessment
                                    ? "w-full max-w-none"
                                    : "text-base sm:text-2xl max-w-2xl w-full"
                                }`}
                              >
                                {question.title_EN}
                              </h5>
                            )}
                          </Question>

                          {/* Input Options for assessments */}
                          {isColorBindnessAssessment && (
                            <QuizOption
                              option={question?.questionOption}
                              question={question}
                              index={index}
                              currentIndex={currentIndex}
                              isQuizComplete={isQuizComplete}
                              userAnswers={userAnswers}
                              handleAnswerChange={handleAnswerChange}
                              isProcessing={
                                isProcessing || !imageLoaded[question.id]
                              }
                              currentLocale={currentLocale}
                            />
                          )}
                          {!isColorBindnessAssessment && (
                            <LabelContainer
                              className={`relative mb-28 max-w-screen-sm sm:max-w-full ${
                                optionCount === 2
                                  ? "flex-row items-center justify-center"
                                  : "flex-col md:flex-row items-end md:items-center"
                              }`}
                            >
                              <LabelText
                                className={`text-[#36A2B8] ${
                                  optionCount === 2
                                    ? "mr-6"
                                    : "absolute left-0 md:left-auto bottom-0 md:bottom-auto md:relative"
                                }`}
                              >
                                {t("quiz.disagree")}
                              </LabelText>
                              <CircleContainer className="mx-auto mb-3 flex md:gap-5 sm:gap-4 gap-2">
                                {Array.from(Array(optionCount), (_, i) => (
                                  <label key={i} className="text-center">
                                    <input
                                      id={`quiz-input-${index + 1}-option-${i + 1}`}
                                      type="radio"
                                      name={`question-${question.id}`}
                                      className="hidden"
                                      onChange={(e) =>
                                        handleAnswerChange(
                                          e,
                                          question.id,
                                          index,
                                          undefined,
                                        )
                                      }
                                      checked={
                                        userAnswers[question.id] === i + 1
                                      }
                                      value={i + 1}
                                      disabled={
                                        index > currentIndex || isQuizComplete
                                      }
                                    />
                                    <div
                                      className={`rounded-full p-0 flex mx-0 justify-center items-center border-2 ${
                                        isQuizComplete || index > currentIndex
                                          ? "cursor-not-allowed"
                                          : "cursor-pointer"
                                      }
                      ${getQuizPaddingByIndex(i, optionCount)}
                      bg-[#f5f5f5] hover:bg-insightful-light-purple transition-all duration-300`}
                                      style={{
                                        borderColor:
                                          userAnswers[question.id] === i + 1
                                            ? "#805AED"
                                            : getQuizColorByIndex(
                                                i,
                                                optionCount,
                                              ).color,
                                        backgroundColor:
                                          userAnswers[question.id] === i + 1
                                            ? "#805AED"
                                            : "",
                                      }}
                                    >
                                      {/* Display checkmark if answer is selected */}
                                      {userAnswers[question.id] === i + 1 && (
                                        <span>
                                          <svg
                                            className="w-7 fill-white"
                                            clipRule="evenodd"
                                            fillRule="evenodd"
                                            strokeLinejoin="round"
                                            strokeMiterlimit="2"
                                            viewBox="0 0 24 24"
                                            xmlns="http://www.w3.org/2000/svg"
                                          >
                                            <path
                                              d="m2.25 12.321 7.27 6.491c.143.127.321.19.499.19.206 0 .41-.084.559-.249l11.23-12.501c.129-.143.192-.321.192-.5 0-.419-.338-.75-.749-.75-.206 0-.411.084-.559.249l-10.731 11.945-6.711-5.994c-.144-.127-.322-.19-.5-.19-.417 0-.75.336-.75.749 0 .206.084.412.25.56"
                                              fillRule="nonzero"
                                            />
                                          </svg>
                                        </span>
                                      )}
                                    </div>
                                  </label>
                                ))}
                              </CircleContainer>
                              <LabelText
                                className={`text-[#53AF65] ${
                                  optionCount === 2 ? "ml-6" : ""
                                }`}
                              >
                                {t("quiz.agree")}
                              </LabelText>
                            </LabelContainer>
                          )}
                        </>
                      )}
                    </form>
                  </QuestionContainer>
                );
              })}

              {isAllQuestionsAnswered() &&
                !isLoading &&
                !userData?.gender &&
                renderGenderSection()}
              {isAllQuestionsAnswered() &&
                !isLoading &&
                renderCompleteTestButton()}
            </div>
          </Container>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default Quiz;

const Container = styled.div`
  width: ${(props) => (props.isColorBlind ? "100vw" : "100%")};
  display: flex;
  justify-content: center;
  align-items: ${(props) =>
    props.isColorBlind || props.isIQTest ? "center" : "flex-start"};
  items: center;
  flex-direction: column;
  padding: ${(props) =>
    props.isColorBlind || props.isIQTest ? "0" : "0 15px"};
  min-height: auto;
  ${"" /* height: ${(props) => (props.isColorBlind ? "72vh" : "auto")}; */}
  height : auto;
`;

const LabelContainer = styled.div`
  display: flex;
  margin: 1rem 0;
`;

const LabelText = styled.div`
  font-size: 1rem;
  font-weight: bold;
  white-space: nowrap;
  margin: 0 1rem;

  @media (max-width: 640px) {
    font-size: 0.8rem;
    font-weight: bold;
    white-space: nowrap;
    margin: 0 1rem;
  }
`;

const CircleContainer = styled.div`
  display: flex;
  align-items: center;
`;

// const ButtonContainer = styled.div`
//   display: flex;
//   justify-content: ${({ currentIndex }) =>
//     currentIndex === 0 ? "flex-end" : "space-between"};
//   padding-left: 1rem;
//   width: 100%;
// `;

const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const QuestionContainer = styled.div``;

// const QuestionContainer = styled.div`
//   animation: ${fadeIn} 0.5s ease-in-out forwards;
//   opacity: 1;
//   ${(props) =>
//     props.animation &&
//     css`
//       animation-delay: ${props.animation * 0.5}s;
//       animation-fill-mode: forwards;
//     `}
// `;
const Question = styled.div`
  ${({ animation }) =>
    animation
      ? css`
          animation: ${fadeIn} 0.5s ease-in-out forwards;
        `
      : ""}
  opacity: 0.5s;
`;
